import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, BellOff } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotifications, requestNotificationPermission, currentNotificationPermission } from '../hooks/useNotifications';
import { ensurePushSubscribed } from '../hooks/usePushRegistration';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from './ui/dropdown-menu';

// Format an ISO timestamp as a friendly "3m ago" / "2h ago" label.
const timeAgo = (iso, lang) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60)   return lang === 'ar' ? `منذ ${s}ث` : `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)   return lang === 'ar' ? `منذ ${m}د` : `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)   return lang === 'ar' ? `منذ ${h}س` : `${h}h ago`;
  const days = Math.floor(h / 24);
  return lang === 'ar' ? `منذ ${days}ي` : `${days}d ago`;
};

export const NotificationBell = ({ activeMode = false }) => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications({ language, enabled: true, activeMode });
  const [permission, setPermission] = useState(currentNotificationPermission());

  // Ask for permission once when the bell mounts (browser suppresses repeat
  // prompts after the user's decision). When granted, also register the
  // Web-Push subscription so the user gets real OS-level pushes.
  useEffect(() => {
    if (permission === 'default') {
      requestNotificationPermission().then((p) => {
        setPermission(p);
        if (p === 'granted') ensurePushSubscribed();
      });
    } else if (permission === 'granted') {
      ensurePushSubscribed();
    }
  }, [permission]);

  const handleClick = async (n) => {
    if (!n.read) await markRead(n.id);
    if (n.link) navigate(n.link);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative w-10 h-10 rounded-full hover:bg-slate-100 active:scale-95 flex items-center justify-center transition"
          data-testid="notification-bell"
          aria-label={t('الإشعارات', 'Notifications')}
        >
          <Bell size={20} className="text-slate-700" />
          {unreadCount > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="notification-unread-count"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[380px] max-h-[520px] overflow-hidden p-0"
        data-testid="notification-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-white sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <Bell size={16} className="text-[#1565c0]" />
            <span className="font-bold text-slate-800">{t('الإشعارات', 'Notifications')}</span>
            {unreadCount > 0 && (
              <span className="text-xs text-red-600 font-semibold">
                {unreadCount} {t('غير مقروء', 'unread')}
              </span>
            )}
          </div>
          {notifications.length > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="text-xs text-[#1565c0] hover:underline flex items-center gap-1 font-semibold"
              data-testid="notification-mark-all-read"
            >
              <CheckCheck size={14} />
              {t('تعيين الكل كمقروء', 'Mark all read')}
            </button>
          )}
        </div>

        {/* Browser permission hint */}
        {permission === 'denied' && (
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2 text-xs text-amber-800">
            <BellOff size={14} />
            {t('إشعارات المتصفح معطلة. فعّلها من إعدادات المتصفح.', 'Browser notifications are blocked. Enable them from your browser settings.')}
          </div>
        )}

        {/* Empty state */}
        {notifications.length === 0 && (
          <div className="p-8 text-center text-slate-500 text-sm" data-testid="notification-empty">
            <Bell size={28} className="mx-auto mb-2 text-slate-300" />
            {t('لا توجد إشعارات', 'No notifications yet')}
          </div>
        )}

        {/* List */}
        <div className="overflow-y-auto max-h-[440px]">
          {notifications.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => handleClick(n)}
              className={`w-full text-left flex gap-3 px-4 py-3 border-b border-slate-100 transition ${
                n.read ? 'bg-white hover:bg-slate-50' : 'bg-blue-50/50 hover:bg-blue-50'
              }`}
              data-testid={`notification-item-${n.id}`}
            >
              <div className="flex-shrink-0 mt-1">
                <span className={`inline-flex w-2 h-2 rounded-full ${n.read ? 'bg-transparent' : 'bg-blue-600'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-sm font-bold ${n.read ? 'text-slate-700' : 'text-slate-900'} truncate`}>
                    {language === 'ar' ? (n.title_ar || n.title_en) : (n.title_en || n.title_ar)}
                  </p>
                  <span className="text-[10px] text-slate-400 flex-shrink-0">
                    {timeAgo(n.created_at, language)}
                  </span>
                </div>
                <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                  {language === 'ar' ? (n.message_ar || n.message_en) : (n.message_en || n.message_ar)}
                </p>
              </div>
              {!n.read && (
                <span className="flex-shrink-0" title={t('تعيين كمقروء', 'Mark as read')}>
                  <Check size={14} className="text-slate-400" />
                </span>
              )}
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
